import type { ApiResponse } from "apisauce"

import { request } from "../../../../../services/api"
import { unwrapEnvelope } from "../../../../../utils/api/unwrap-envelope.ts"
import {
  filenameFromContentDisposition,
  saveBlob
} from "../../../../../utils/download-file.ts"

import {
  type ConflictSolverState,
  type LinkableRecord,
  type PlanSheet,
  type SowChatTurn,
  type SowDocument,
  type SowProjectTypeOption,
  type SowIssue,
  type SowSourceFile,
  type SowVersion,
  type SowWorkItem
} from "./services.types.ts"

const BASE_URL = "/plans-sow"

interface ErrorDetail {
  detail?: string
}

// Same envelope-unwrap pattern as the ReceiptFinancialLine section's own
// services.ts (EnvelopeJSONRenderer wraps every successful write as
// `{ status, details: { data } }`; GET responses are bare, and
// unwrapEnvelope is a no-op on an already-bare payload). A failure is read
// for its own `detail` message before falling back to the generic one.
async function unwrap<T>(
  requestPromise: Promise<ApiResponse<T, ErrorDetail>>,
  errorMessage: string
): Promise<T> {
  const response = await requestPromise
  if (response.ok && response.data) {
    return unwrapEnvelope<T>(response.data)
  }
  const detail = !response.ok ? response.data?.detail : undefined
  throw new Error(detail || errorMessage)
}

export function createDocument(title: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/`, { title }),
    "Create failed"
  )
}

export function uploadPlanSource(
  id: string,
  file: File
): Promise<{ id: string; page_count: number }> {
  const form = new FormData()
  form.append("file", file)
  return unwrap<{ id: string; page_count: number }>(
    request.api.post<{ id: string; page_count: number }, ErrorDetail>(
      `${BASE_URL}/${id}/source/`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } }
    ),
    "Upload failed"
  )
}

export function uploadReferenceSource(
  id: string,
  file: File
): Promise<{ id: string }> {
  const form = new FormData()
  form.append("file", file)
  return unwrap<{ id: string }>(
    request.api.post<{ id: string }, ErrorDetail>(
      `${BASE_URL}/${id}/reference/`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" }
      }
    ),
    "Upload failed"
  )
}

// Same bare-204-no-body shape as `deleteDocument` — DELETE returns nothing
// to unwrap, so this bypasses `unwrap` for the same reason that one does.
export async function deleteReferenceSource(id: string): Promise<void> {
  const response = await request.api.delete<undefined, ErrorDetail>(
    `${BASE_URL}/${id}/reference/`
  )
  if (!response.ok) {
    throw new Error(response.data?.detail || "Remove failed")
  }
}

export function updateDocument(
  id: string,
  data: Partial<SowDocument>
): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.patch<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/`, data),
    "Update failed"
  )
}

export function generateDocument(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/generate/`),
    "Generate failed"
  )
}

export function cancelDocument(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/cancel/`),
    "Cancel failed"
  )
}

export function approveDocument(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/approve/`),
    "Approve failed"
  )
}

export function startChat(id: string, message: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/chat/`, {
      message
    }),
    "Request failed"
  )
}

// Not routed through `unwrap` — DELETE returns a bare 204 with no response
// body at all, so `unwrap`'s `response.data` truthiness check would read a
// genuinely successful delete as a failure.
export async function deleteDocument(id: string): Promise<void> {
  const response = await request.api.delete<undefined, ErrorDetail>(
    `${BASE_URL}/${id}/`
  )
  if (!response.ok) {
    throw new Error(response.data?.detail || "Delete failed")
  }
}

// Not routed through `unwrap` — a successful response is a Blob, not the
// envelope-wrapped JSON `unwrap` expects, and the filename lives in a
// response header rather than the body. A blob-typed request's error body
// can come back EITHER as a real Blob (containing the JSON error text) or as
// already-parsed JSON, depending on axios/apisauce's content-type sniffing —
// the same ambiguity `Invoices/api/invoices.services.ts`'s own
// `exportInvoices` (via `invoices.thunks.ts`'s `exportInvoicesList`) has to
// handle, so both shapes are read here too.
// `versionId` picks one version; without it the server serves the official
// version, else the latest.
export async function downloadDocument(
  id: string,
  fallbackName: string,
  versionId?: string
): Promise<void> {
  const response = await request.api.get<Blob, ErrorDetail>(
    `${BASE_URL}/${id}/download/`,
    versionId ? { version: versionId } : {},
    { responseType: "blob" }
  )
  if (!response.ok || !response.data) {
    let detail: string | undefined
    if (response.data instanceof Blob) {
      try {
        const text = await response.data.text()
        detail = JSON.parse(text)?.detail
      } catch {
        // ignore — fall through to the generic message
      }
    } else {
      detail = (response.data as ErrorDetail | undefined)?.detail
    }
    throw new Error(detail || "Download failed")
  }
  // Every version of a document is stored under the same file name, so a
  // per-version download names the version itself.
  const filename = versionId
    ? fallbackName
    : filenameFromContentDisposition(
        response.headers as Record<string, string> | undefined
      ) || fallbackName
  saveBlob(response.data, filename)
}

export async function downloadPdf(
  id: string,
  filename: string,
  versionId?: string
): Promise<void> {
  saveBlob(await fetchPreviewBlob(id, versionId), filename)
}

// Same blob-fetch pattern as `downloadDocument` above, but returns the Blob
// itself (for a `URL.createObjectURL` preview) instead of saving it to disk.
export async function fetchPreviewBlob(
  id: string,
  versionId?: string
): Promise<Blob> {
  const response = await request.api.get<Blob, ErrorDetail>(
    `${BASE_URL}/${id}/preview/`,
    versionId ? { version: versionId } : {},
    { responseType: "blob" }
  )
  if (!response.ok || !response.data) {
    let detail: string | undefined
    if (response.data instanceof Blob) {
      try {
        const text = await response.data.text()
        detail = JSON.parse(text)?.detail
      } catch {
        // ignore — fall through to the generic message
      }
    } else {
      detail = (response.data as ErrorDetail | undefined)?.detail
    }
    throw new Error(detail || "Preview failed")
  }
  return response.data
}

interface CurrentOfficialVersionBody {
  detail: string
  requires_confirmation: true
  current_official_version: SowVersion
}

export interface ApproveVersionResult {
  requiresConfirmation: boolean
  version?: SowVersion
  currentOfficialVersion?: SowVersion
}

// Not routed through `unwrap`: a 409 here is a normal branch of this flow
// (another version is already official — the caller decides whether to
// swap), not a failure to surface as a thrown error the way every other
// non-2xx response in this file is.
export async function approveVersion(
  documentId: string,
  versionId: string,
  confirmed = false
): Promise<ApproveVersionResult> {
  const response = await request.api.post<
    SowVersion | CurrentOfficialVersionBody,
    ErrorDetail
  >(
    `${BASE_URL}/${documentId}/versions/${versionId}/approve/`,
    confirmed ? { confirmed: true } : {}
  )

  if (response.ok && response.data) {
    return {
      requiresConfirmation: false,
      version: unwrapEnvelope<SowVersion>(response.data)
    }
  }
  if (
    response.status === 409 &&
    response.data &&
    "requires_confirmation" in response.data
  ) {
    return {
      requiresConfirmation: true,
      currentOfficialVersion: response.data.current_official_version
    }
  }
  throw new Error(
    (response.data as ErrorDetail | undefined)?.detail || "Approve failed"
  )
}

// Bare 204, same reason `deleteDocument` bypasses `unwrap`.
export async function deleteVersion(
  documentId: string,
  versionId: string
): Promise<void> {
  const response = await request.api.delete<undefined, ErrorDetail>(
    `${BASE_URL}/${documentId}/versions/${versionId}/`
  )
  if (!response.ok) {
    throw new Error(response.data?.detail || "Delete failed")
  }
}

export function getDocument(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.get<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/`),
    "Fetch failed"
  )
}

export function listDocuments(): Promise<{
  results: SowDocument[]
  count: number
}> {
  return unwrap<{ results: SowDocument[]; count: number }>(
    request.api.get<{ results: SowDocument[]; count: number }, ErrorDetail>(
      `${BASE_URL}/`
    ),
    "List failed"
  )
}

interface ReviewResponse {
  sheets: PlanSheet[]
  issues: SowIssue[]
  folder_name: string
  files: SowSourceFile[]
  work_items: SowWorkItem[]
  // Optional so every pre-existing test fixture built before EZ-Chat shipped
  // stays valid without an update; ReviewScreen falls back to an empty list.
  chat_turns?: SowChatTurn[]
  // Same reason: optional so fixtures predating the version manager stay
  // valid; ReviewScreen falls back to an empty list.
  versions?: SowVersion[]
}

export function getReview(id: string): Promise<ReviewResponse> {
  return unwrap<ReviewResponse>(
    request.api.get<ReviewResponse, ErrorDetail>(`${BASE_URL}/${id}/review/`),
    "Review failed"
  )
}

interface LinkableRecordsResponse {
  count: number
  next: string | null
  previous: string | null
  results: LinkableRecord[]
}

export function searchLinkableRecords(params: {
  q?: string
  // Re-reads one record by id (the one a document is already linked to),
  // even after it stopped being active.
  id?: string
  entity_type?: "all" | "project" | "lead"
  page?: number
  page_size?: number
}): Promise<LinkableRecordsResponse> {
  return unwrap<LinkableRecordsResponse>(
    request.api.get<LinkableRecordsResponse, ErrorDetail>(
      `${BASE_URL}/linkable-records/`,
      params
    ),
    "Search failed"
  )
}

export function getConflictSolver(
  documentId: string
): Promise<ConflictSolverState> {
  return unwrap<ConflictSolverState>(
    request.api.get<ConflictSolverState, ErrorDetail>(
      `${BASE_URL}/${documentId}/conflict-solver/`
    ),
    "Could not load suggestions"
  )
}

export function startConflictSolver(
  documentId: string
): Promise<ConflictSolverState> {
  return unwrap<ConflictSolverState>(
    request.api.post<ConflictSolverState, ErrorDetail>(
      `${BASE_URL}/${documentId}/conflict-solver/start/`
    ),
    "Could not start the AI Conflict Solver"
  )
}

export function adoptSuggestion(
  documentId: string,
  suggestionId: string,
  text: string
): Promise<ConflictSolverState> {
  return unwrap<ConflictSolverState>(
    request.api.post<ConflictSolverState, ErrorDetail>(
      `${BASE_URL}/${documentId}/conflict-solver/suggestions/${suggestionId}/adopt/`,
      { text }
    ),
    "Could not adopt the suggestion"
  )
}

export function ignoreSuggestion(
  documentId: string,
  suggestionId: string
): Promise<ConflictSolverState> {
  return unwrap<ConflictSolverState>(
    request.api.post<ConflictSolverState, ErrorDetail>(
      `${BASE_URL}/${documentId}/conflict-solver/suggestions/${suggestionId}/ignore/`
    ),
    "Could not ignore the suggestion"
  )
}

export function regenerateDocument(documentId: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(
      `${BASE_URL}/${documentId}/regenerate/`
    ),
    "Could not regenerate the SOW"
  )
}

export function getSowProjectTypes(): Promise<SowProjectTypeOption[]> {
  return unwrap<SowProjectTypeOption[]>(
    request.api.get<SowProjectTypeOption[], ErrorDetail>(
      `${BASE_URL}/project-types/`
    ),
    "Could not load project types"
  )
}
